"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { gsap } from "gsap";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { Logo } from "./Logo";
import { ProductIcon } from "./Sections";

// Screen copy (names, titles, tables) comes from the dictionary
// (preview.screens); only presentation details are kept here.
const SCREEN_ICONS = { personal: [3, 0, 1, 3], business: [3, 3, 3, 4] } as const;
const INTERVAL = 4800;
const FIRST_INTERVAL = 2800;

const technologyMarks: Record<string, string> = {
  "Ubuntu Linux": "ubuntu",
  "Debian Linux": "debian",
};

function PreviewValue({ value }: { value: string }) {
  const mark = technologyMarks[value];
  if (mark) {
    return (
      <span className="ed-preview-value">
        <span
          className={`ed-preview-mark ed-preview-mark-${mark}`}
          aria-hidden="true"
        />
        <span>{value}</span>
      </span>
    );
  }
  if (/^10\.|^TCP \/|^app\//.test(value)) {
    return <span className="ed-preview-technical">{value}</span>;
  }
  return <>{value}</>;
}

type CloudShellExtension = {
  trigger: ReactNode;
  panel: ReactNode;
  toolbar: ReactNode;
};

export function ProductPreview({
  business = false,
  cloudShell,
}: {
  business?: boolean;
  cloudShell?: CloudShellExtension;
}) {
  const presentation = Boolean(cloudShell);
  const { t: dict } = useI18n();
  const t = dict.preview;
  const audience = business ? "business" : "personal";
  const screens = t.screens[audience].map((s, i) => ({
    ...s,
    icon: SCREEN_ICONS[audience][i] ?? 3,
    topology: business && i === 0,
  }));
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [passed, setPassed] = useState(false);
  const advanced = useRef(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [reduced, setReduced] = useState(true);
  const [ready, setReady] = useState(false);
  const root = useRef<HTMLElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();
  const screen = screens[active];
  const playing =
    ready && !paused && !passed && pageVisible && !reduced && !cloudShell;
  useEffect(() => {
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReduced(motion.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion();
    updateVisibility();
    const observer = new IntersectionObserver(
      (entries) => setPassed(entries[0].boundingClientRect.bottom <= 0),
      { threshold: 0 },
    );
    if (root.current) observer.observe(root.current);
    const timer = setTimeout(() => setReady(true), 0);
    motion.addEventListener("change", updateMotion);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      motion.removeEventListener("change", updateMotion);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);
  useEffect(() => {
    if (!playing) return;
    const timer = setTimeout(
      () => {
        advanced.current = true;
        setActive((i) => (i + 1) % screens.length);
      },
      advanced.current ? INTERVAL : FIRST_INTERVAL,
    );
    return () => clearTimeout(timer);
  }, [playing, active, screens.length]);
  useEffect(() => {
    if (reduced || presentation) return;
    // Scope inline animation styles so navigation and reduced motion restore cleanly.
    const context = gsap.context(() => {
      gsap.fromTo(
        ".ed-tour-panel > *",
        { autoAlpha: 0, y: 12 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.48,
          stagger: 0.065,
          ease: "power2.out",
          clearProps: "opacity,visibility,transform",
        },
      );
    }, panel);
    return () => context.revert();
  }, [active, reduced, presentation]);
  function select(index: number) {
    setActive(index);
    setPaused(true);
  }
  const consoleContent = (
    <>
      <div className="ed-console-bar">
        <Logo />
        <span className="ed-bar-crumb">
          {t.workspace} <i>/</i> {business ? t.businessApp : t.personalApp}
        </span>
        <span className="ed-tour-sample">{t.sample}</span>
        {cloudShell?.trigger}
      </div>
      <div className="ed-console-body">
        <aside className="ed-console-side">
          <small>{business ? t.sideBusiness : t.sidePersonal}</small>
          <div
            className="ed-tour-tabs"
            role={cloudShell ? undefined : "tablist"}
            aria-label={cloudShell ? undefined : t.tabs}
          >
            {screens.map((s, i) =>
              cloudShell ? (
                <div
                  key={s.name}
                  className={`ed-tour-tab-static ${active === i ? "is-active" : ""}`}
                >
                  <ProductIcon index={s.icon} />
                  <span>{s.name}</span>
                  <span className="ed-tour-tab-dot" aria-hidden="true" />
                </div>
              ) : (
                <button
                  key={s.name}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  id={`${id}-tab-${i}`}
                  role="tab"
                  aria-selected={active === i}
                  aria-controls={`${id}-panel`}
                  tabIndex={active === i ? 0 : -1}
                  className={active === i ? "is-active" : ""}
                  onClick={() => select(i)}
                  onKeyDown={(e) => {
                    let next = i;
                    if (e.key === "ArrowRight" || e.key === "ArrowDown")
                      next = (i + 1) % screens.length;
                    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
                      next = (i + screens.length - 1) % screens.length;
                    else if (e.key === "Home") next = 0;
                    else if (e.key === "End") next = screens.length - 1;
                    else return;
                    e.preventDefault();
                    select(next);
                    tabRefs.current[next]?.focus();
                  }}
                >
                  <ProductIcon index={s.icon} />
                  <span>{s.name}</span>
                  <span className="ed-tour-tab-dot" aria-hidden="true" />
                </button>
              ),
            )}
          </div>
          {business && (
            <div className="ed-tour-sidebar-note">{t.sidebarNote}</div>
          )}
        </aside>
        <div className="ed-console-main">
          <div
            ref={panel}
            id={`${id}-panel`}
            role={cloudShell ? undefined : "tabpanel"}
            aria-labelledby={cloudShell ? undefined : `${id}-tab-${active}`}
            tabIndex={cloudShell ? undefined : 0}
            className="ed-tour-panel"
          >
            <div className="ed-console-heading">
              <div>
                <span className="ed-console-crumb">
                  {business ? t.crumbBusiness : t.crumbPersonal} / {screen.name}
                </span>
                <h3>{screen.title}</h3>
                <p>{screen.description}</p>
              </div>
            </div>
            <div className="ed-console-summary">
              {screen.summary.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>
                    <PreviewValue value={value} />
                  </strong>
                </div>
              ))}
            </div>
            {screen.topology && (
              <div
                className="ed-tour-topology"
                role="img"
                aria-label={t.topology}
              >
                <span>{"application-net"}</span>
                <div>
                  {t.topologyNodes.map((node) => (
                    <span key={node}>{node}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="ed-tour-table-wrap">
              <table className="ed-tour-table">
                <caption className="ed-tour-table-caption">
                  {active === 0 && !business ? t.selected : screen.name} ·{" "}
                  {t.sampleConfig}
                </caption>
                <thead>
                  <tr>
                    {screen.columns.map((label, i) => (
                      <th
                        className={
                          i === 1
                            ? "ed-tour-secondary"
                            : i === 2
                              ? "ed-tour-tertiary"
                              : undefined
                        }
                        key={label}
                        scope="col"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {screen.rows.map((row) => (
                    <tr key={row[0]}>
                      {row.map((value, i) => (
                        <td
                          className={
                            i === 1
                              ? "ed-tour-secondary"
                              : i === 2
                                ? "ed-tour-tertiary"
                                : undefined
                          }
                          key={i}
                        >
                          {i === 3 ? (
                            <span
                              className={`ed-tour-state ${value === t.stopped ? "ed-tour-state-muted" : ""}`}
                            >
                              {value}
                            </span>
                          ) : (
                            <PreviewValue value={value} />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="ed-tour-footer">
            <div className="ed-tour-position" aria-hidden="true">
              {screens.map((s, i) => (
                <i key={s.name} className={active === i ? "is-active" : ""} />
              ))}
              <span>{String(active + 1).padStart(2, "0")} / 04</span>
            </div>
            {!cloudShell && ready && !reduced && (
              <button
                type="button"
                className="ed-tour-control"
                aria-label={paused ? t.resumeLabel : t.pauseLabel}
                onClick={() => setPaused((p) => !p)}
              >
                <svg
                  className="ed-tour-control-icon"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  aria-hidden="true"
                >
                  {paused ? (
                    <path d="M2.5 1.2v7.6L8.6 5z" />
                  ) : (
                    <path d="M2 1h2v8H2zM6 1h2v8H6z" />
                  )}
                </svg>
                {paused ? t.play : t.pause}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
  return (
    <figure
      className="ed-stage ed-tour"
      data-audience={business ? "business" : "personal"}
      ref={root}
      aria-label={
        cloudShell
          ? t.figureShell
          : business
            ? t.figureBusiness
            : t.figurePersonal
      }
      onFocusCapture={(e) => {
        // Reading or choosing a view is deliberate interaction. Hover alone isn't.
        if (e.target.closest('[role="tab"], [role="tabpanel"]'))
          setPaused(true);
      }}
    >
      <div className="ed-stage-orbit ed-stage-orbit-one" aria-hidden="true" />
      <div className="ed-stage-orbit ed-stage-orbit-two" aria-hidden="true" />
      {cloudShell && (
        <div className="ed-tour-toolbar">{cloudShell.toolbar}</div>
      )}
      <div className="ed-console">
        {cloudShell ? (
          <>
            <div className="ed-console-app">{consoleContent}</div>
            {cloudShell.panel}
          </>
        ) : (
          consoleContent
        )}
      </div>
    </figure>
  );
}
